import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useCallback } from "react"
import {
	CouponForm,
	type CouponFormValues,
} from "@/components/coupons/coupon-form"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminCoupon, useUpdateCoupon } from "@/queries/marketplace"

export const Route = createFileRoute("/coupons/$couponId/edit")({
	component: EditCouponPage,
})

function EditCouponPage() {
	const { couponId } = Route.useParams()
	const navigate = useNavigate()
	const { coupon, isLoading, error } = useAdminCoupon({ couponId })
	const updateMutation = useUpdateCoupon()

	const handleSubmit = useCallback(
		async (values: CouponFormValues) => {
			await updateMutation.mutateAsync({
				id: couponId,
				description: values.description,
				value: values.value,
				currency: values.currency,
				region: values.region,
				applicableCurrencies: values.applicableCurrencies,
				currencyAmounts: values.currencyAmounts,
				maxRedemptions: values.maxRedemptions,
				maxRedemptionsPerUser: values.maxRedemptionsPerUser,
				minimumOrderAmount: values.minimumOrderAmount,
				maximumDiscountAmount: values.maximumDiscountAmount,
				startsAt: values.startsAt.toISOString(),
				expiresAt: values.expiresAt?.toISOString() ?? null,
				newBuyersOnly: values.newBuyersOnly,
				accountMustPredateCoupon: values.accountMustPredateCoupon,
				audience: values.audience,
			})
			navigate({
				to: "/coupons/$couponId",
				params: { couponId },
			})
		},
		[couponId, updateMutation, navigate],
	)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Skeleton className="h-8 w-64" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		)
	}

	if (error || !coupon) {
		return (
			<div className="flex flex-col items-start gap-3 p-6">
				<p className="text-bad">
					{error?.message || "The coupon could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/coupons" search={{} as never}>
						<ArrowLeft />
						Back to coupons
					</Link>
				</Button>
			</div>
		)
	}

	return (
		<div className="p-6">
			<CouponForm
				mode="edit"
				coupon={coupon}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
			/>
		</div>
	)
}
